import { Signal, computed, signal } from '@angular/core';

/**
 * Lifecycle state of a dialog.  Exposed as a signal so consumers can react
 * via `effect()` instead of subscribing to an Observable.
 */
export interface NbxDialogState<T> {
  status: 'open' | 'closed';
  value: T | undefined;
}

/**
 * Handle returned by {@link NbxDialogService.open}.  Pure-signal API — no rxjs.
 *
 * Modal components inject this token and call {@link close} to dismiss themselves
 * and pass back a result.  Callers of `open()` react to closure with an `effect`
 * watching {@link isClosed} (or via {@link ModalState}'s `result` signal).
 */
export class NbxDialogRef<T = unknown> {
  private readonly _state = signal<NbxDialogState<T>>({ status: 'open', value: undefined });
  private _afterCloseCallbacks: ((value: T | undefined) => void)[] = [];

  readonly state: Signal<NbxDialogState<T>> = this._state.asReadonly();

  readonly isOpen = computed(() => this._state().status === 'open');

  readonly isClosed = computed(() => this._state().status === 'closed');

  readonly value = computed(() => this._state().value);

  /**
   * Register a one-shot callback fired when the dialog closes.  Use this
   * as the primary mechanism for reacting to a modal result — it avoids
   * the cross-effect interference that plagues shared result signals.
   *
   * If the dialog is already closed, the callback runs synchronously.
   * Multiple callbacks are supported and run in registration order.
   *
   * @returns `this` for chaining (e.g. `service.open(...).afterClosed(...)`).
   */
  afterClosed(callback: (value: T | undefined) => void): this {
    if (this._state().status === 'closed') {
      callback(this._state().value);
      return this;
    }
    this._afterCloseCallbacks.push(callback);
    return this;
  }

  /**
   * Close the dialog and (optionally) emit a result.
   * Subsequent calls are no-ops — the state transition is one-shot.
   */
  close(value?: T): void {
    if (this._state().status === 'closed') {
      return;
    }
    this._state.set({ status: 'closed', value });
    const callbacks = this._afterCloseCallbacks;
    this._afterCloseCallbacks = [];
    for (const cb of callbacks) {
      cb(value);
    }
  }
}
